import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from './Spinner';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  as?: 'button';
  /** When provided, renders a react-router <Link> styled as a button. */
  to?: string;
  /**
   * When provided (and `to` is not), renders a plain <a> styled as a button —
   * for URLs outside the SPA, such as a maps link. A real link keeps the
   * browser's own affordances (middle-click, copy link address, status-bar
   * preview) that a button calling window.open() throws away.
   */
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: string;
};

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-primary text-white hover:bg-primary-dark',
  secondary: 'bg-secondary text-white hover:bg-secondary/90',
  outline: 'border border-primary text-primary-strong hover:bg-primary/10',
  ghost: 'text-primary-strong hover:bg-primary/10',
  danger: 'bg-error text-white hover:bg-error/90',
};

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-3 text-base',
  lg: 'px-5 py-4 text-lg',
};

const spinnerSizes: Record<NonNullable<ButtonProps['size']>, number> = {
  sm: 16,
  md: 18,
  lg: 20,
};

/**
 * Primary action button. 5 variants x 3 sizes. If `to` is given (and the
 * button is not disabled/loading) it renders a react-router <Link> with the
 * same styling; if `href` is given instead, a plain <a>.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  as: _as,
  to,
  href,
  target,
  rel,
  className,
  disabled,
  children,
  type,
  ...rest
}: ButtonProps) {
  const classes = cn(
    'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl font-bold transition-all duration-200',
    variantClasses[variant],
    sizeClasses[size],
    'disabled:cursor-not-allowed disabled:opacity-50',
    className,
  );

  const content = (
    <>
      {loading ? <Spinner size={spinnerSizes[size]} /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </>
  );

  if ((to || href) && !disabled && !loading) {
    // Button-flavoured attributes overlap anchor attributes for everything we
    // forward in practice (onClick, aria-*, data-*, title, ...).
    const anchorRest = rest as unknown as Omit<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      'href'
    >;
    if (to) {
      return (
        <Link to={to} target={target} rel={rel} className={classes} {...anchorRest}>
          {content}
        </Link>
      );
    }
    return (
      <a href={href} target={target} rel={rel} className={classes} {...anchorRest}>
        {content}
      </a>
    );
  }

  return (
    <button
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
}
